import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Factory, BadgeCheck, Truck, Store, Loader2, ShieldCheck, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import { market, PARTNER_LABELS } from '../lib/api';
import { useAuth } from '../lib/auth';
import PartnerDocUpload from '../components/PartnerDocUpload';

const TYPES = [
  { id: 'manufacturer', Icon: Factory, desc: 'You make products in your own factory and want to sell directly to customers.' },
  { id: 'brand_owner', Icon: BadgeCheck, desc: 'You own a registered brand and want an official brand store.' },
  { id: 'distributor', Icon: Truck, desc: 'You are authorised by brands to sell their genuine products.' },
  { id: 'retailer', Icon: Store, desc: 'You run a shop or business and want to sell online across India.' },
];
const CONSTITUTIONS = [['proprietorship', 'Proprietorship'], ['partnership', 'Partnership firm'], ['llp', 'LLP'], ['private_limited', 'Private Limited'], ['public_limited', 'Public Limited'], ['opc', 'One Person Company'], ['other', 'Other']];
const OPTIONAL_DOCS = ['gst_certificate', 'udyam_certificate', 'cancelled_cheque', 'manufacturing_licence', 'certification', 'product_photo', 'product_catalogue', 'business_registration'];
const EMPTY = { company_name: '', constitution: 'proprietorship', owner_name: '', contact_person: '', mobile: '', email: '', registered_address: '', facility_address: '', city: '', state: '', pincode: '', pan: '', gstin: '', udyam: '', bank_account: '', bank_ifsc: '', bank_holder_name: '', categories: [], brand_names: '', trademark_numbers: '', licences: '', website: '', description: '' };
const STATUS = { pending: ['Under review', Clock, 'text-[#E67E22]'], more_info: ['More information needed', Clock, 'text-[#D35400]'], approved: ['Approved', CheckCircle2, 'text-[#1B5E20]'], rejected: ['Rejected', XCircle, 'text-[#B71C1C]'] };

function Field({ label, k, form, set, required, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink/70">{label}{required && <span className="text-[#B71C1C]"> *</span>}</span>
      <input data-testid={`partner-${k}`} value={form[k]} onChange={e => set(k, e.target.value)} required={required}
        className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm bg-white focus:border-[#E67E22] outline-none" {...rest}/>
    </label>
  );
}

function MyApplications({ items }) {
  if (!items.length) return null;
  return (
    <section data-testid="my-partner-applications" className="card-soft p-4 space-y-2">
      <div className="font-heading font-bold">Your applications</div>
      {items.map(a => {
        const [label, Icon, cls] = STATUS[a.status] || STATUS.pending;
        return (
          <div key={a.id} data-testid={`my-application-${a.partner_type}`} className="flex flex-wrap items-center gap-2 text-sm border-t border-border pt-2">
            <b>{PARTNER_LABELS[a.partner_type]}</b> · {a.company_name}
            <span className={`inline-flex items-center gap-1 font-semibold ${cls}`}><Icon className="w-4 h-4"/>{label}</span>
            {a.admin_note && <span className="w-full text-xs text-ink/70">Note from BharatCart: {a.admin_note}</span>}
            {a.status === 'approved' && <Link to="/dashboard" className="text-xs underline text-[#D35400]">Open seller dashboard</Link>}
          </div>
        );
      })}
    </section>
  );
}

export default function PartnerRegister() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const type = sp.get('type');
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { market.meta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { if (user) market.myApplications().then(d => setMine(d.items)).catch(() => {}); }, [user]);
  useEffect(() => { if (user) setForm(f => ({ ...f, mobile: f.mobile || user.phone || '', email: f.email || user.email || '', owner_name: f.owner_name || user.name || '' })); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.categories.length) return toast.error('Pick at least one product category');
    setBusy(true);
    try {
      await market.apply({ ...form, partner_type: type, brand_names: form.brand_names.split(',').map(s => s.trim()).filter(Boolean), documents: docs });
      toast.success('Application submitted. We will review it within 2 working days.');
      setForm(EMPTY); setDocs([]); setSp({});
      market.myApplications().then(d => setMine(d.items));
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Could not submit'); }
    finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center space-y-4" data-testid="partner-login-required">
        <ShieldCheck className="w-10 h-10 mx-auto text-[#E67E22]"/>
        <h1 className="font-heading font-black text-3xl">Become a BharatCart partner</h1>
        <p className="text-ink/70">Please sign in or create an account first. Your application is linked to your login.</p>
        <button data-testid="partner-login-btn" className="btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('mandispot:open-auth', { detail: { mode: 'signup' } }))}>Sign in / Sign up</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6" data-testid="partner-register-page">
      <div>
        <h1 className="font-heading font-black text-3xl md:text-4xl">Register on BharatCart</h1>
        <p className="text-ink/70 mt-1">Choose how you want to sell. Every partner is verified by our team before going live.</p>
      </div>
      <MyApplications items={mine}/>
      <div className="grid sm:grid-cols-2 gap-3">
        {TYPES.map(({ id, Icon, desc }) => (
          <button key={id} type="button" data-testid={`partner-type-${id}`} onClick={() => setSp({ type: id })}
            className={`text-left rounded-2xl border-2 p-4 transition-colors ${type === id ? 'border-[#E67E22] bg-[#FFF8EE]' : 'border-border bg-white hover:border-[#E67E22]/60'}`}>
            <Icon className="w-6 h-6 text-[#D35400]"/>
            <div className="font-heading font-bold mt-2">{PARTNER_LABELS[id]}</div>
            <div className="text-sm text-ink/70">{desc}</div>
          </button>
        ))}
      </div>
      {type && meta && (
        <form onSubmit={submit} className="card-soft p-5 space-y-5" data-testid="partner-form">
          <div className="font-heading font-bold text-lg">{PARTNER_LABELS[type]} details</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Company / business name" k="company_name" form={form} set={set} required/>
            <label className="block"><span className="text-xs font-semibold text-ink/70">Business constitution *</span>
              <select data-testid="partner-constitution" value={form.constitution} onChange={e => set('constitution', e.target.value)} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm bg-white">
                {CONSTITUTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <Field label="Owner / director name" k="owner_name" form={form} set={set} required/>
            <Field label="Contact person" k="contact_person" form={form} set={set} required/>
            <Field label="Mobile (10 digits)" k="mobile" form={form} set={set} required inputMode="numeric" maxLength={10}/>
            <Field label="Email" k="email" form={form} set={set} required type="email"/>
            <div className="md:col-span-2"><Field label="Registered business address" k="registered_address" form={form} set={set} required/></div>
            {type === 'manufacturer' && <div className="md:col-span-2"><Field label="Manufacturing facility address" k="facility_address" form={form} set={set} required/></div>}
            <Field label="City" k="city" form={form} set={set} required/>
            <Field label="State" k="state" form={form} set={set} required/>
            <Field label="PIN code" k="pincode" form={form} set={set} required inputMode="numeric" maxLength={6}/>
            <Field label="Website (optional)" k="website" form={form} set={set}/>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="PAN" k="pan" form={form} set={set} required maxLength={10} placeholder="ABCDE1234F"/>
            <Field label="GSTIN (if registered)" k="gstin" form={form} set={set} maxLength={15}/>
            <Field label="Udyam / MSME no. (optional)" k="udyam" form={form} set={set} placeholder="UDYAM-TS-01-1234567"/>
            <Field label="Bank account number" k="bank_account" form={form} set={set} required inputMode="numeric"/>
            <Field label="IFSC" k="bank_ifsc" form={form} set={set} required maxLength={11}/>
            <Field label="Account holder name" k="bank_holder_name" form={form} set={set} required/>
          </div>
          <p className="text-xs text-ink/60 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#1B5E20]"/>PAN and bank details are encrypted and never shown publicly.</p>
          {type !== 'retailer' && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label={type === 'distributor' ? 'Brands you are authorised for (comma separated)' : 'Brand names you own (comma separated)'} k="brand_names" form={form} set={set} required={type !== 'manufacturer'}/>
              <Field label="Trademark numbers (if any)" k="trademark_numbers" form={form} set={set}/>
              {type === 'manufacturer' && <div className="md:col-span-2"><Field label="Manufacturing licences & certifications" k="licences" form={form} set={set}/></div>}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-ink/70 mb-2">Product categories *</div>
            <div className="flex flex-wrap gap-2">
              {meta.categories.map(c => {
                const on = form.categories.includes(c);
                return <button type="button" key={c} data-testid={`partner-cat-${c}`} onClick={() => set('categories', on ? form.categories.filter(x => x !== c) : [...form.categories, c])}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? 'bg-[#E67E22] text-white border-[#E67E22]' : 'bg-white border-border hover:border-[#E67E22]'}`}>{c}</button>;
              })}
            </div>
          </div>
          <label className="block"><span className="text-xs font-semibold text-ink/70">About your business</span>
            <textarea data-testid="partner-description" value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"/></label>
          <div>
            <div className="text-xs font-semibold text-ink/70 mb-2">Documents (JPG, PNG or PDF, max 8 MB each)</div>
            <div className="grid md:grid-cols-2 gap-2">
              {[...meta.required_docs[type], ...OPTIONAL_DOCS.filter(k => !meta.required_docs[type].includes(k) && (type === 'manufacturer' || !['manufacturing_licence'].includes(k)))].map(k => (
                <PartnerDocUpload key={k} kind={k} required={meta.required_docs[type].includes(k)} docs={docs}
                  onAdd={d => setDocs(x => [...x, d])} onRemove={u => setDocs(x => x.filter(d => d.url !== u))}/>
              ))}
            </div>
          </div>
          <button data-testid="partner-submit-btn" disabled={busy} className="btn-primary w-full md:w-auto">{busy && <Loader2 className="w-4 h-4 animate-spin"/>}Submit for verification</button>
        </form>
      )}
    </div>
  );
}
